import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import VisaTabs from "../AllTabs/VisaTabs";
import moment from "moment";
import DatePicker from "react-datepicker"; // Import the new date picker
import "react-datepicker/dist/react-datepicker.css"; // Import the styles
import { RiCalendarLine } from "react-icons/ri";
import "./VisaRequests.css";

const VisaRequestReports = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const isInitialMount = useRef(true);

  // Filters state
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
    supplierId: "",
    visaRequestStatus: "",
    paymentStatus: "",
  });

  // Format date for API
  const formatDateForAPI = (date) => {
    return moment(date).format("YYYY-MM-DD");
  };

  // Get current month date range
  const getCurrentMonthDateRange = () => {
    const startOfMonth = moment().startOf("month");
    const endOfMonth = moment().endOf("month");

    return {
      fromDate: startOfMonth.toDate(),
      toDate: endOfMonth.toDate(),
    };
  };

  // Fetch suppliers for filter dropdown
  // const GetSuppliers = async () => {
  //   try {
  //     const res = await apiService.getAll(`${config.GETALLSUPPLIERS}`);
  //     if (res && res.length > 0) {
  //       setSuppliers(res);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching suppliers:", error);
  //   }
  // };

  // Fetch report data
  const GetData = async (currentFilters) => {
    if (!user?.cid) return;

    setIsLoading(true);
    try {
      let url = `${config.GETVISAREQUESTREPORTBYCOMPANY}/${user.cid}`;

      const params = new URLSearchParams();

      // Add filters to params
      Object.keys(currentFilters).forEach((key) => {
        const value = currentFilters[key];
        if (value && value !== "" && value !== "all") {
          if (key === "fromDate" || key === "toDate") {
            // Format date for API
            params.append(key, new Date(value).toISOString());
          } else {
            params.append(key, value);
          }
        }
      });

      // Add date filters with proper formatting
      if (currentFilters.fromDate) {
        params.append(
          "fromDate",
          new Date(currentFilters.fromDate).toISOString()
        );
      }
      if (currentFilters.toDate) {
        params.append("toDate", new Date(currentFilters.toDate).toISOString());
      }

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const res = await apiService.getAll(url);
      setReports(res || []);
    } catch (error) {
      console.error("Error fetching visa report:", error);
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle date changes
  const handleDateChange = (date, field) => {
    setFilters((prev) => ({
      ...prev,
      [field]: date,
    }));
  };

  // Reset filters
  const resetFilters = () => {
    const defaultDates = getCurrentMonthDateRange();
    setFilters({
      fromDate: defaultDates.fromDate,
      toDate: defaultDates.toDate,
      supplierId: "",
      visaRequestStatus: "",
      paymentStatus: "",
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    const totals = {
      totalSupplierPrice: 0,
      totalGST: 0,
      totalMarkup: 0,
      totalCustomerPrice: 0,
      totalPrice: 0,
      totalApplicants: 0,
    };

    reports.forEach((item) => {
      totals.totalSupplierPrice += parseFloat(item.supplierPrice) || 0;
      totals.totalGST += parseFloat(item.gst) || 0;
      totals.totalMarkup += parseFloat(item.markUP) || 0;
      totals.totalCustomerPrice += parseFloat(item.customerPrice) || 0;
      totals.totalPrice += parseFloat(item.totalPrice) || 0;
      totals.totalApplicants += parseInt(item.noOfApplicants) || 0;
    });

    return totals;
  };

  // Status badge classes
  const getStatusBadgeClass = (status) => {
    if (!status) return "badge bg-secondary";

    switch (status.toUpperCase()) {
      case "APPROVED":
        return "badge bg-success";
      case "PENDING":
      case "PROCESSING":
      case "DOCUMENT_PENDING":
        return "badge bg-warning text-dark";
      case "REJECTED":
        return "badge bg-danger";
      case "SUBMITTED":
      case "NEW":
        return "badge bg-info";
      case "DOCUMENTS_MISSING":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  const getPaymentBadgeClass = (status) => {
    if (!status) return "badge bg-secondary";

    switch (status.toUpperCase()) {
      case "PAID":
        return "badge bg-success";
      case "PENDING":
        return "badge bg-warning text-dark";
      case "FAILED":
      case "CANCELLED":
        return "badge bg-danger";
      case "REFUNDED":
        return "badge bg-info";
      default:
        return "badge bg-secondary";
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "0.00";
    return parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Custom input component for date picker
  const CustomDateInput = React.forwardRef(
    ({ value, onClick, placeholder }, ref) => (
      <div className="input-group">
        <input
          type="text"
          className="form-control form-control-md"
          value={value}
          onClick={onClick}
          ref={ref}
          readOnly
          placeholder={placeholder}
        />
        <button className="btn btn-secondary" type="button" onClick={onClick}>
          <RiCalendarLine />
        </button>
      </div>
    )
  );

  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      const defaultDates = getCurrentMonthDateRange();
      setFilters((prev) => ({
        ...prev,
        fromDate: defaultDates.fromDate,
        toDate: defaultDates.toDate,
      }));
      // GetSuppliers();
      return;
    }

    const debounceTimer = setTimeout(() => {
      GetData(filters);
    }, 350);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [user, filters]);

  const totals = calculateTotals();

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <VisaTabs pageName="VISA REQUEST REPORTS" />
              </div>
              <div className="card-body">
                {/* Filters Section - Updated with new calendar */}
                <div className="row g-2 mb-3">
                  <div className="col-xl-2 col-md-4">
                    <DatePicker
                      selected={filters.fromDate}
                      onChange={(date) => handleDateChange(date, "fromDate")}
                      customInput={<CustomDateInput placeholder="From Date" />}
                      dateFormat="dd-MMM-yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={50}
                      scrollableYearDropdown
                      className="form-control form-control-md"
                      selectsStart
                      startDate={filters.fromDate}
                      endDate={filters.toDate}
                    />
                  </div>

                  <div className="col-xl-2 col-md-4">
                    <DatePicker
                      selected={filters.toDate}
                      onChange={(date) => handleDateChange(date, "toDate")}
                      customInput={<CustomDateInput placeholder="To Date" />}
                      dateFormat="dd-MMM-yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={50}
                      scrollableYearDropdown
                      className="form-control form-control-md"
                      selectsEnd
                      startDate={filters.fromDate}
                      endDate={filters.toDate}
                      minDate={filters.fromDate}
                    />
                  </div>

                  {/* <div className="col-xl-2 col-md-4">
                    <select
                      className="form-select form-select-md"
                      name="supplierId"
                      value={filters.supplierId}
                      onChange={handleFilterChange}
                    >
                      <option value="all">All Suppliers</option>
                      {suppliers.map((supplier) => (
                        <option
                          key={supplier.supplierId}
                          value={supplier.supplierId}
                        >
                          {supplier.supplierName}
                        </option>
                      ))}
                    </select>
                  </div> */}

                  <div className="col-xl-2 col-md-4">
                    <select
                      className="form-select form-select-md"
                      name="visaRequestStatus"
                      value={filters.visaRequestStatus}
                      onChange={handleFilterChange}
                    >
                      <option value="all">All Visa Statuses</option>
                      <option value="New">New</option>
                      <option value="PENDING">Pending</option>
                      <option value="PROCESSING">Processing</option>
                      <option value="SUBMITTED">Submitted</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="DOCUMENTS_MISSING">
                        Documents Missing
                      </option>
                      <option value="DOCUMENT_PENDING">Document Pending</option>
                    </select>
                  </div>

                  <div className="col-xl-2 col-md-4">
                    <select
                      className="form-select form-select-md"
                      name="paymentStatus"
                      value={filters.paymentStatus}
                      onChange={handleFilterChange}
                    >
                      <option value="all">All Payment Statuses</option>
                      <option value="PAID">Paid</option>
                      <option value="PENDING">Pending</option>
                      <option value="FAILED">Failed</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="REFUNDED">Refunded</option>
                    </select>
                  </div>

                  <div className="col-xl-2 col-md-4 d-flex align-items-start">
                    <button
                      className="btn btn-md btn-danger"
                      onClick={resetFilters}
                    >
                      <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                      Reset
                    </button>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading visa reports...</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Reference No</th>
                            <th>Invoice No</th>
                            <th>Visa</th>
                            <th>Supplier</th>
                            <th>Customer</th>
                            <th>Supplier Price</th>

                            <th>Markup</th>
                            <th>Customer Price</th>

                            <th>Visa Status</th>
                            <th>Customer Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reports && reports.length > 0 ? (
                            reports.map((report, index) => (
                              <tr key={index}>
                                <td className="fw-semibold">
                                  {report.visaReferenceNo}
                                </td>
                                <td>{report.invoiceNo || "-"}</td>
                                <td
                                  style={{
                                    whiteSpace: "normal",
                                    wordWrap: "break-word",
                                    maxWidth: "150px",
                                  }}
                                >
                                  <div className="fw-semibold">
                                    {report.visaName}
                                  </div>
                                  <small className="text-muted">
                                    {report.countryName}
                                  </small>
                                </td>
                                <td>
                                  <div>
                                    {report.supplierName || "Not assigned"}
                                  </div>
                                </td>
                                <td>
                                  <div>{report.applicantName || "-"}</div>
                                  <div className="text-danger">
                                    No. of Applicants:{" "}
                                    {report.noOfApplicants || 0}
                                  </div>
                                </td>
                                <td>
                                  {report.supplierPrice ? (
                                    <span className="fw-bold text-primary">
                                      INR{formatCurrency(report.supplierPrice)}
                                    </span>
                                  ) : (
                                    <span className="fw-bold text-primary">
                                      -
                                    </span>
                                  )}
                                </td>

                                <td>
                                  {report.markUP ? (
                                    <span className="fw-bold text-success">
                                      INR{formatCurrency(report.markUP)}
                                    </span>
                                  ) : (
                                    <span className="fw-bold text-success">
                                      -
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {report.customerPrice ? (
                                    <span className="fw-bold text-warning">
                                      INR{formatCurrency(report.customerPrice)}
                                    </span>
                                  ) : (
                                    <span className="fw-bold text-warning">
                                      -
                                    </span>
                                  )}
                                </td>

                                <td>
                                  <span
                                    className={getStatusBadgeClass(
                                      report.visaRequestStatus
                                    )}
                                  >
                                    {report.visaRequestStatus}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className={getPaymentBadgeClass(
                                      report.paymentStatus
                                    )}
                                  >
                                    {report.paymentStatus}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="12" className="text-center py-4">
                                No visa reports found
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {/* Grand Total Row */}
                        {reports.length > 0 && (
                          <tfoot className="table-active">
                            <tr className="fw-bold">
                              <td colSpan="5" className="text-end">
                                Grand Total:
                              </td>
                              <td className="text-primary">
                                INR{formatCurrency(totals.totalSupplierPrice)}
                              </td>

                              <td className="text-success">
                                INR{formatCurrency(totals.totalMarkup)}
                              </td>
                              <td className="text-warning">
                                INR{formatCurrency(totals.totalCustomerPrice)}
                              </td>

                              <td colSpan="2"></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaRequestReports;
